import { useEffect, useState } from "react";
import { bootstrapEssential } from "@/lib/observability";
import { useTrackPageView } from "@/hooks/useTrackPageView";

interface Props {
  children: React.ReactNode;
}

/**
 * Single root-level provider for analytics:
 *   - Bootstraps essential SDKs (Sentry + vitals) without blocking render.
 *   - Auto-tracks SPA pageviews (must live inside Router).
 *   - Exposes loading status if a child needs it.
 */
export function AnalyticsProvider({ children }: Props): JSX.Element {
  const [, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    bootstrapEssential()
      .then(() => {
        if (cancelled) return;
        setReady(true);
        // Sprint 3: UX instrumentation (rage/dead click, scroll depth,
        // form abandonment). Idempotent; deferred to idle to keep TTI clean.
        const start = () => {
          import("@/lib/observability/ux")
            .then((m) => m.initUxInstrumentation())
            .catch(() => undefined);
        };
        const w = window as unknown as {
          requestIdleCallback?: (fn: () => void, opts?: { timeout: number }) => void;
        };
        if (typeof w.requestIdleCallback === "function") w.requestIdleCallback(start, { timeout: 3000 });
        else setTimeout(start, 1500);
      })
      .catch(() => {
        /* swallow — analytics must never break the app */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useTrackPageView();

  return <>{children}</>;
}
